/**
 * Maintenance mode, as Mr.Roboto holds it.
 *
 * The flag lives in the bot's own table and is what stops a restart going
 * through, so a window opened here is the same act as one opened with
 * `/server` in Discord: the bot makes the change and logs it in the same
 * channel. Argus never talks to the bot directly — argus-backend checks the
 * staff member's permission and holds the token.
 *
 * A failed read is never reported as "nothing is in maintenance". The status
 * page would then draw a planned window as an outage, which is the one thing
 * this is meant to prevent.
 *
 * A window comes in two kinds. Plain maintenance is the familiar one: the pack
 * is down for work. "Coming soon" is the same flag underneath (restarts are
 * refused, the whitelist is on) worn by a new server that is shown to the
 * public before it opens, optionally with the moment it is expected to. The
 * bot keeps the kind and the release time beside the flag, so a coming-soon
 * server is always also `true` in `servers`, and a client that has never heard
 * of the second kind still sees it as maintenance rather than as an outage.
 */

const API_BASE_URL = 'https://spy.playcdu.co/api/notes';

/** Server address to whether it is in maintenance. Addresses are lowercase. */
export type MaintenanceMap = Record<string, boolean>;

/** The two kinds of window. The wire spelling is the bot's. */
export type MaintenanceMode = 'maintenance' | 'coming_soon';

/**
 * One coming-soon server. `releaseAt` is unix seconds, or null when staff have
 * not named a time; it is a promise to the public, not a timer: the server
 * opens when somebody ends the window, whatever the clock says.
 */
export interface ComingSoonEntry {
  releaseAt: number | null;
}

/** Server address to its coming-soon entry. Only coming-soon servers appear. */
export type ComingSoonMap = Record<string, ComingSoonEntry>;

export interface MaintenanceResult {
  timestamp: number;
  servers: MaintenanceMap;
  /** Always present; empty when nothing is coming soon or the bot predates the mode. */
  comingSoon: ComingSoonMap;
}

/** What a start may say about the window beyond "on". */
export interface MaintenanceOptions {
  /** Defaults to plain maintenance, as the bot does. */
  mode?: MaintenanceMode;
  /** Unix seconds, in the future. Only meaningful with `coming_soon`; null clears it. */
  releaseAt?: number | null;
}

export interface MaintenanceChange {
  timestamp: number;
  maintenance: boolean;
  /** Servers whose flag actually moved. */
  changed: string[];
  /** Servers already in the requested state. */
  unchanged: string[];
  /** Addresses the bot does not know. */
  unknown: string[];
  /** Whether the change reached the Discord log channel. */
  announced: boolean;
  /** The kind of window now in force; null when the window was ended. Absent from a bot that predates it. */
  mode?: MaintenanceMode | null;
  /** The release time now in force, unix seconds. */
  release_at?: number | null;
}

export class MaintenanceApiError extends Error {
  readonly status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'MaintenanceApiError';
    this.status = status;
  }
}

const addressKey = (address: string) => address.trim().toLowerCase();

/** A usable release time, or null: the wire may carry anything, and 0 is not a date anyone meant. */
export function releaseSeconds(value: unknown): number | null {
  const seconds = typeof value === 'number' ? value : typeof value === 'string' && value.trim() ? Number(value) : NaN;
  return Number.isFinite(seconds) && seconds > 0 ? Math.floor(seconds) : null;
}

/**
 * The GET body, made safe to draw from.
 *
 * `coming_soon` is meant to be a subset of the servers that are `true`. Where
 * the two disagree the coming-soon entry still implies the flag, because a
 * server shown as "opening soon" is by definition not open, and drawing it as
 * neither would leave the reader with an unexplained dark server. An explicit
 * `false` wins, though: the flag is what the bot enforces.
 */
export function parseMaintenanceBody(body: unknown): MaintenanceResult {
  const raw = (body && typeof body === 'object' ? body : {}) as {
    timestamp?: unknown; servers?: unknown; coming_soon?: unknown;
  };
  const servers: MaintenanceMap = {};
  if (raw.servers && typeof raw.servers === 'object') {
    for (const [address, state] of Object.entries(raw.servers as Record<string, unknown>)) {
      const key = addressKey(address);
      if (key) servers[key] = Boolean(state);
    }
  }
  const comingSoon: ComingSoonMap = {};
  if (raw.coming_soon && typeof raw.coming_soon === 'object') {
    for (const [address, entry] of Object.entries(raw.coming_soon as Record<string, unknown>)) {
      const key = addressKey(address);
      if (!key || servers[key] === false) continue;
      const releaseAt = entry && typeof entry === 'object' ? releaseSeconds((entry as { release_at?: unknown }).release_at) : null;
      comingSoon[key] = { releaseAt };
      servers[key] = true;
    }
  }
  return { timestamp: Number(raw.timestamp) || Date.now() / 1000, servers, comingSoon };
}

/**
 * The state a change leaves behind, painted before the next poll confirms it.
 *
 * Only the servers the bot answered for are touched. The kind and release time
 * come from the bot's answer where it gave one, and from the request where it
 * did not (an older bot), in which case the next poll is what corrects it.
 */
export function mergeMaintenanceChange(
  previous: Pick<MaintenanceResult, 'servers' | 'comingSoon'> | null,
  change: MaintenanceChange,
  requested: { maintenance: boolean } & MaintenanceOptions,
): Pick<MaintenanceResult, 'servers' | 'comingSoon'> {
  const servers: MaintenanceMap = { ...(previous?.servers ?? {}) };
  const comingSoon: ComingSoonMap = { ...(previous?.comingSoon ?? {}) };
  const mode = change.mode !== undefined ? change.mode : requested.maintenance ? requested.mode ?? 'maintenance' : null;
  const releaseAt = change.release_at !== undefined ? releaseSeconds(change.release_at) : releaseSeconds(requested.releaseAt);
  for (const address of [...change.changed, ...change.unchanged]) {
    const key = addressKey(address);
    servers[key] = requested.maintenance;
    if (requested.maintenance && mode === 'coming_soon') comingSoon[key] = { releaseAt };
    else delete comingSoon[key];
  }
  return { servers, comingSoon };
}

class MaintenanceApiService {
  async getMaintenance(signal?: AbortSignal): Promise<MaintenanceResult> {
    const response = await fetch(`${API_BASE_URL}/maintenance`, { signal });
    if (!response.ok) {
      throw new MaintenanceApiError(
        response.status === 503
          ? 'Maintenance state is unavailable.'
          : `Maintenance state could not be read (${response.status}).`,
        response.status,
      );
    }
    return parseMaintenanceBody(await response.json());
  }

  /**
   * Start or end a window on one server or a whole cluster.
   *
   * Starting a window on a server already in one re-states it: that is how a
   * coming-soon server moves its release time, or turns into plain
   * maintenance. Ending always ends both kinds; the bot clears the release
   * time with the flag.
   */
  async setMaintenance(servers: string[], maintenance: boolean, options: MaintenanceOptions = {}): Promise<MaintenanceChange> {
    const addresses = Array.from(new Set(servers.map(addressKey).filter(Boolean)));
    if (addresses.length === 0) {
      throw new MaintenanceApiError('No server was named.', 400);
    }

    const body: Record<string, unknown> = { servers: addresses, maintenance };
    if (maintenance && options.mode) {
      body.mode = options.mode;
      // A release time only means something on a server that is not open yet.
      if (options.mode === 'coming_soon' && options.releaseAt !== undefined) {
        const releaseAt = releaseSeconds(options.releaseAt);
        if (releaseAt !== null && releaseAt <= Date.now() / 1000) {
          throw new MaintenanceApiError('The release time must be in the future.', 400);
        }
        body.release_at = releaseAt;
      }
    }

    const response = await fetch(`${API_BASE_URL}/maintenance`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      const detail = await response.json().catch(() => null) as { error?: string } | null;
      throw new MaintenanceApiError(
        detail?.error
          ?? (response.status === 403
            ? 'You do not have permission to change maintenance mode.'
            : 'The maintenance change could not be applied.'),
        response.status,
      );
    }
    return await response.json() as MaintenanceChange;
  }
}

export const maintenanceApiService = new MaintenanceApiService();
